import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal",
    "title", "datePresets", "dateFrom", "dateTo",
    "responsible", "companiesInvolved",
    "progressOfWork", "graphTimeDaily", "graphTimeWeekly",
    "lateActivities", "controleNotaFiscal", "pendingAis",
    "commentInput", "commentsList", "addCommentBtn",
    "exportBtn", "exportSpinner"
  ]

  static values = {
    exportUrl: String,
    generatingToast: String,
    readyToast: String
  }

  connect() {
    this._comments = []
    this._currentPreset = "7"
    this._dateStart = null
    this._dateEnd = null
    this._pollTimers = []
  }

  disconnect() {
    this._pollTimers.forEach(t => clearTimeout(t))
    this._pollTimers = []
  }

  // ── Modal ──────────────────────────────────────────────────────────

  open(event) {
    event.preventDefault()
    document.dispatchEvent(new CustomEvent("dropdown:close"))
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    // Apply default preset (Últimos 7 Dias)
    this._applyPresetDates("7")
  }

  hideModal(event) {
    if (event.target === this.modalTarget) {
      this._close()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  close() {
    this._close()
  }

  _close() {
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  // ── Date Presets ───────────────────────────────────────────────────

  selectDatePreset(event) {
    const preset = event.currentTarget.dataset.preset
    this._currentPreset = preset
    this._applyPresetDates(preset)
    this._updateDatePresetChips()
  }

  filterDatesCustom() {
    this._currentPreset = ""
    this._updateDatePresetChips()
    this._dateStart = this.dateFromTarget.value
    this._dateEnd = this.dateToTarget.value
  }

  _applyPresetDates(preset) {
    const today = new Date()
    let from = null
    let to = null

    if (preset === "today") {
      from = new Date(today)
      to = new Date(today)
    } else if (preset === "yesterday") {
      const d = new Date(today)
      d.setDate(d.getDate() - 1)
      from = d
      to = new Date(d)
    } else if (preset === "7" || preset === "30") {
      const days = parseInt(preset)
      const d = new Date(today)
      d.setDate(d.getDate() - (days - 1))
      from = d
      to = new Date(today)
    } else if (preset === "this_month") {
      from = new Date(today.getFullYear(), today.getMonth(), 1)
      to = new Date(today)
    } else if (preset === "last_month") {
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      to = new Date(today.getFullYear(), today.getMonth(), 0)
    }

    if (from && to) {
      this._dateStart = this._formatDate(from)
      this._dateEnd = this._formatDate(to)

      // Update the visible date inputs
      if (this.hasDateFromTarget) this.dateFromTarget.value = this._dateStart
      if (this.hasDateToTarget) this.dateToTarget.value = this._dateEnd
    }
  }

  _updateDatePresetChips() {
    if (!this.hasDatePresetsTarget) return
    const buttons = this.datePresetsTarget.querySelectorAll("button")
    const activeClasses = ["bg-blue-50", "text-blue-600", "font-medium", "border-blue-200"]
    const inactiveClasses = ["text-gray-500", "hover:bg-gray-50", "border-gray-200"]

    buttons.forEach((btn) => {
      const isActive = btn.dataset.preset === this._currentPreset
      btn.classList.remove(...activeClasses, ...inactiveClasses)
      if (isActive) {
        btn.classList.add(...activeClasses)
      } else {
        btn.classList.add(...inactiveClasses)
      }
    })
  }

  _formatDate(d) {
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}`
  }

  // ── Comments ───────────────────────────────────────────────────────

  validateComment() {
    const hasText = this.commentInputTarget.value.trim().length > 0
    const btn = this.addCommentBtnTarget
    if (hasText) {
      btn.classList.remove("pointer-events-none", "opacity-40")
    } else {
      btn.classList.add("pointer-events-none", "opacity-40")
    }
  }

  addComment(event) {
    event.preventDefault()
    const text = this.commentInputTarget.value.trim()
    if (!text) return

    this._comments.push(text)
    this._renderComments()
    this.commentInputTarget.value = ""
    this.addCommentBtnTarget.classList.add("pointer-events-none", "opacity-40")
  }

  removeComment(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this._comments.splice(index, 1)
    this._renderComments()
  }

  _renderComments() {
    this.commentsListTarget.innerHTML = this._comments.map((c, i) => `
      <div class="flex items-start gap-2 bg-gray-50 rounded-lg px-3 py-2">
        <p class="flex-1 text-[13px] text-gray-700 whitespace-pre-wrap">${this._escapeHtml(c)}</p>
        <a href="#" data-action="click->kpi-report#removeComment" data-index="${i}"
           class="shrink-0 text-gray-400 hover:text-red-500 cursor-pointer mt-0.5">
          <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
          </svg>
        </a>
      </div>
    `).join("")
  }

  // ── Export ──────────────────────────────────────────────────────────

  async exportPdf() {
    if (!this._dateStart || !this._dateEnd) {
      this._showToast("Selecione o período.", "error")
      return
    }

    this.exportBtnTarget.disabled = true
    this.exportSpinnerTarget.classList.remove("hidden")

    const graphTime = this.hasGraphTimeDailyTarget && this.graphTimeDailyTarget.checked ? "daily" : "weekly"

    const params = new URLSearchParams()
    params.append("title", this.titleTarget.value)
    params.append("date_start", this._dateStart)
    params.append("date_end", this._dateEnd)
    if (this.responsibleTarget.value) params.append("responsible", this.responsibleTarget.value)
    if (this.companiesInvolvedTarget.value) params.append("companies_involved", this.companiesInvolvedTarget.value)
    if (this.progressOfWorkTarget.checked) {
      params.append("progress_of_work", "on")
      params.append("progress_of_work_graph_time", graphTime)
    }
    if (this.lateActivitiesTarget.checked) params.append("late_activities", "on")
    if (this.controleNotaFiscalTarget.checked) params.append("controle_nota_fiscal", "on")
    if (this.pendingAisTarget.checked) params.append("pending_ais", "on")
    this._comments.forEach(c => params.append("comments[]", c))

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(this.exportUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json"
        },
        body: params.toString()
      })

      if (!resp.ok) {
        const text = await resp.text().catch(() => "")
        throw new Error(text || "Erro ao gerar PDF")
      }

      const data = await resp.json()
      this._close()

      const toastMsg = this.hasGeneratingToastValue && this.generatingToastValue
        ? this.generatingToastValue
        : "Generating report. Check below in a moment."
      this._showGeneratingToast(toastMsg)

      if (data.report) {
        const readyMsg = this.hasReadyToastValue && this.readyToastValue
          ? this.readyToastValue
          : "KPI Report is ready."
        document.dispatchEvent(new CustomEvent("special-report:created", {
          detail: { report: data.report, readyMessage: readyMsg }
        }))

        if (data.report.poll_url) {
          this._pollForCompletion(data.report.id, data.report.poll_url, readyMsg)
        }
      }
    } catch (e) {
      console.error("KPI export error:", e)
      this._showToast(e.message || "Erro ao gerar relatório.", "error")
    } finally {
      this.exportBtnTarget.disabled = false
      this.exportSpinnerTarget.classList.add("hidden")
    }
  }

  // ── Polling ─────────────────────────────────────────────────────────

  _pollForCompletion(reportId, pollUrl, readyMsg) {
    const poll = async () => {
      try {
        const csrf = document.querySelector('meta[name="csrf-token"]')?.content
        const resp = await fetch(pollUrl, {
          headers: { "Accept": "application/json", "X-CSRF-Token": csrf },
          credentials: "same-origin"
        })
        const data = await resp.json()

        if (data.processing === 0) {
          document.dispatchEvent(new CustomEvent("special-report:ready", {
            detail: { reportId: String(reportId) }
          }))
          this._hideGeneratingToast()
          this._showToast(readyMsg, "notice")
          return
        }

        const timer = setTimeout(poll, 3000)
        this._pollTimers.push(timer)
      } catch (e) {
        console.error("Poll failed:", e)
        const timer = setTimeout(poll, 5000)
        this._pollTimers.push(timer)
      }
    }

    const timer = setTimeout(poll, 3000)
    this._pollTimers.push(timer)
  }

  _showGeneratingToast(message) {
    this._hideGeneratingToast()
    const el = document.createElement("div")
    el.id = "special-report-generating-toast"
    el.className = "flash-toast-info"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "0")
    el.setAttribute("role", "status")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = `<svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>`
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _hideGeneratingToast() {
    const existing = document.getElementById("special-report-generating-toast")
    if (existing) existing.remove()
  }

  // ── Helpers ────────────────────────────────────────────────────────

  _escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
